import React, { useState } from "react";
import {
  IconSearch,
  IconFilter,
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconRotate,
  IconFileText,
  IconBox,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";

const MOVEMENT_TYPES = [
  { value: "all", label: "All Movements" },
  { value: "ISSUE", label: "Issues (To Custody)" },
  { value: "RETURN", label: "Returns (To Store)" },
  { value: "SETTLED_SALE", label: "POS Settled Sales" },
  { value: "CONSUMPTION", label: "Non-Sale Usage / Wastage" },
  { value: "RECONCILIATION_ADJUSTMENT", label: "Audit Adjustments" },
];

export default function MovementHistoryView({
  movements,
  total,
  page,
  limit,
  isLoading,
  filters,
  onFilterChange,
  onPageChange,
}) {
  const [searchInput, setSearchInput] = useState(filters.search || "");

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onFilterChange({ ...filters, search: searchInput, page: 1 });
  };

  const totalPages = Math.ceil((total || 0) / limit) || 1;

  return (
    <div className="space-y-4">
      {/* Search & Filter Header */}
      <div className="bg-base-100 p-4 rounded-2xl border border-base-200 shadow-sm">
        <form
          onSubmit={handleSearchSubmit}
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[280px]">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <IconSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
                size={18}
                stroke={iconStroke}
              />
              <input
                type="text"
                placeholder="Search reference, product, staff, notes..."
                className="input input-sm input-bordered w-full pl-9 text-xs"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            {/* Movement Type Filter */}
            <select
              className="select select-sm select-bordered text-xs"
              value={filters.movement_type || "all"}
              onChange={(e) =>
                onFilterChange({ ...filters, movement_type: e.target.value, page: 1 })
              }
            >
              {MOVEMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            {/* Date Range */}
            <div className="flex items-center gap-1.5 text-xs">
              <input
                type="date"
                className="input input-sm input-bordered text-xs"
                value={filters.from || ""}
                onChange={(e) =>
                  onFilterChange({ ...filters, from: e.target.value, page: 1 })
                }
              />
              <span className="text-base-content/40 font-bold">to</span>
              <input
                type="date"
                className="input input-sm input-bordered text-xs"
                value={filters.to || ""}
                onChange={(e) =>
                  onFilterChange({ ...filters, to: e.target.value, page: 1 })
                }
              />
            </div>

            <button type="submit" className="btn btn-sm btn-primary">
              Filter
            </button>
            {(filters.search || filters.from || filters.to || (filters.movement_type && filters.movement_type !== "all")) && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  onFilterChange({ search: "", movement_type: "all", from: "", to: "", page: 1 });
                }}
                className="btn btn-sm btn-ghost text-xs"
              >
                Clear
              </button>
            )}
          </div>

          <div className="text-xs text-base-content/60 font-medium">
            Total Movements: <span className="font-bold text-base-content">{total}</span>
          </div>
        </form>
      </div>

      {/* Movement Table */}
      <div className="bg-base-100 rounded-2xl border border-base-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400">
            <IconRotate className="animate-spin mb-3" size={32} stroke={iconStroke} />
            <p className="text-xs">Loading movement history...</p>
          </div>
        ) : movements.length === 0 ? (
          <div className="text-center py-12 px-4">
            <IconFileText className="mx-auto text-base-content/30 mb-2" size={40} stroke={iconStroke} />
            <h4 className="font-bold text-base-content text-sm">No movements found</h4>
            <p className="text-xs text-base-content/60 max-w-sm mx-auto mt-1">
              No stock issue, return, sales, or audit movements match the chosen criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-base-200/50 text-base-content/70 text-xs">
                  <th>Timestamp</th>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Custodian</th>
                  <th>Product</th>
                  <th className="text-right">Entered Qty</th>
                  <th className="text-right">Factor</th>
                  <th className="text-right font-bold text-primary">Base Qty</th>
                  <th>Notes</th>
                  <th>Actor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-200 text-xs">
                {movements.map((m) => {
                  const badgeClass =
                    m.movement_type === "ISSUE"
                      ? "badge-primary"
                      : m.movement_type === "RETURN"
                      ? "badge-success"
                      : m.movement_type === "SETTLED_SALE"
                      ? "badge-info"
                      : m.movement_type === "CONSUMPTION"
                      ? "badge-warning"
                      : "badge-secondary";

                  return (
                    <tr key={m.id} className="hover:bg-base-200/40">
                      <td className="text-[11px] text-base-content/70 whitespace-nowrap">
                        {new Date(m.created_at).toLocaleString([], {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td>
                        <span className={`badge badge-xs font-semibold ${badgeClass}`}>
                          {m.movement_type}
                        </span>
                      </td>
                      <td className="font-mono text-xs font-semibold text-base-content">
                        {m.reference_id || `ID #${m.id}`}
                      </td>
                      <td>
                        <div className="font-semibold text-xs">
                          {m.employee_name || m.user_id}
                        </div>
                        <div className="text-[10px] text-base-content/50">
                          {m.department || "Staff"}
                        </div>
                      </td>
                      <td>
                        <div className="font-medium text-xs text-base-content">
                          {m.item_title}
                        </div>
                        <div className="text-[10px] text-base-content/50">
                          Base: {m.base_unit}
                        </div>
                      </td>
                      <td className="text-right font-mono">
                        {m.quantity} {m.unit_name}
                      </td>
                      <td className="text-right font-mono text-[11px] text-base-content/60">
                        {m.conversion_factor ? `${m.conversion_factor}x` : "1.0x"}
                      </td>
                      <td className="text-right font-mono font-bold text-primary">
                        {Number(m.base_quantity).toLocaleString()}{" "}
                        <span className="text-[10px] font-normal text-base-content/60">
                          {m.base_unit}
                        </span>
                      </td>
                      <td className="max-w-[200px] truncate text-[11px] text-base-content/70" title={m.notes}>
                        {m.notes || "-"}
                      </td>
                      <td className="text-[11px] text-base-content/60">{m.created_by || "system"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-base-200">
            <span className="text-xs text-base-content/60">
              Page {page} of {totalPages}
            </span>
            <div className="join">
              <button
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="join-item btn btn-xs btn-outline"
              >
                <IconChevronLeft size={16} stroke={iconStroke} />
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                className="join-item btn btn-xs btn-outline"
              >
                <IconChevronRight size={16} stroke={iconStroke} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
